import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Chip,
  Avatar,
  Stack,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
  Popover,
  CircularProgress,
  InputAdornment,
  SelectChangeEvent,
  Button,
} from "@mui/material";
import { Badge as BadgeIcon, Edit as EditIcon, Search as SearchIcon } from "@mui/icons-material";
import axiosInstance from "utilities/axios";
import DataTable, { ColumnDef } from "components/common/DataTable";
import ProtectedPage from "components/common/ProtectedPage";
import UserCreateDialog from "components/dialogs/UserCreateDialog";
import { formatUserForDisplay, USER_TYPE_CHIP } from "utils/userFormatting";
import { useAccess } from "hooks/useAccess";
import "./Roles.scss";

type User = { _id: string; name: string; email: string; org_role: string; permissions: string[]; [key: string]: any };

const ORG_ROLE_CHIP: Record<string, { label: string; color: "default" | "primary" | "secondary" }> = {
  head:   { label: "ხელმძღვანელი", color: "primary" },
  member: { label: "წევრი",         color: "default" },
};

function getInitials(name: string) {
  return name.split(" ").slice(0, 2).map((n) => n[0] || "").join("").toUpperCase();
}

function Roles() {
  const [loading, setLoading]           = useState(false);
  const [users, setUsers]               = useState<User[]>([]);
  const [total, setTotal]               = useState(0);
  const [search, setSearch]             = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [filterUserType, setFilterUserType]   = useState("");

  const [savingId, setSavingId]         = useState<string | null>(null);

  // Popover state for role chip click
  const [popoverAnchor, setPopoverAnchor] = useState<HTMLElement | null>(null);
  const [popoverUser, setPopoverUser]     = useState<User | null>(null);

  // Edit user dialog
  const [editUserDialogOpen, setEditUserDialogOpen] = useState(false);
  const [selectedUserForEdit, setSelectedUserForEdit] = useState<User | null>(null);

  const [generalFilterData, setGeneralFilterData] = useState<{ banks: any[]; agency_departments: any[] }>({ banks: [], agency_departments: [] });
  const { isMonitoring } = useAccess();

  useEffect(() => {
    axiosInstance.get("/general_info").then((resp) => setGeneralFilterData(resp.data)).catch(console.error);
  }, []);

  useEffect(() => {
    const handler = setTimeout(() => setDebouncedSearch(search), 500);
    return () => clearTimeout(handler);
  }, [search]);

  const fetchUsers = useCallback(() => {
    setLoading(true);
    axiosInstance
      .post("/fetch_users", { page: 0, limit: 200, search: debouncedSearch, userType: filterUserType || undefined })
      .then((res) => {
        const responseData = res.data.users || res.data;
        const list = (Array.isArray(responseData) ? responseData : []).map(formatUserForDisplay);
        setUsers(list);
        setTotal(res.data.total ?? list.length);
        setLoading(false);
      })
      .catch((err) => { console.error(err); setLoading(false); });
  }, [debouncedSearch, filterUserType]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  function openRolePopover(event: React.MouseEvent<HTMLElement>, user: User) {
    setPopoverAnchor(event.currentTarget);
    setPopoverUser(user);
  }

  function closeRolePopover() {
    setPopoverAnchor(null);
    setPopoverUser(null);
  }

  async function handleSetRole(newRole: "member" | "head") {
    if (!popoverUser) return;
    const userId = popoverUser._id;
    closeRolePopover();
    setSavingId(userId);
    try {
      await axiosInstance.put(`/auth/user/${userId}`, { org_role: newRole });
      fetchUsers();
    } catch (err: any) {
      alert(err?.response?.data?.message || "შეცდომა");
    } finally {
      setSavingId(null);
    }
  }

  const columns: ColumnDef<User>[] = [
    {
      id: "avatar",
      label: "",
      width: 44,
      render: (row) => (
        <Avatar sx={{ width: 30, height: 30, fontSize: 12, bgcolor: row.user_type === "monitoring" ? "secondary.main" : "grey.400" }}>
          {getInitials(row.name || "")}
        </Avatar>
      ),
    },
    {
      id: "name",
      label: "სახელი",
      render: (row) => (
        <>
          <Typography variant="body2" fontWeight={500}>{row.name}</Typography>
          <Typography variant="caption" color="text.secondary">{row.email}</Typography>
        </>
      ),
    },
    {
      id: "type",
      label: "ტიპი",
      align: "center",
      render: (row) =>
        row.user_type && USER_TYPE_CHIP[row.user_type] ? (
          <Chip
            label={USER_TYPE_CHIP[row.user_type].label}
            color={USER_TYPE_CHIP[row.user_type].color}
            size="small"
            variant="outlined"
            sx={{ fontSize: 11, height: 20 }}
          />
        ) : null,
    },
    {
      id: "org",
      label: "ორგანიზაცია",
      render: (row) => <Typography variant="body2">{row.bankDisplay}</Typography>,
    },
    {
      id: "org_role",
      label: "პოზიცია",
      align: "center",
      render: (row) => {
        const roleConfig = ORG_ROLE_CHIP[row.org_role] ?? { label: row.org_role, color: "default" as const };
        if (savingId === row._id) {
          return <CircularProgress size={16} />;
        }
        return (
          <Chip
            label={roleConfig.label}
            color={roleConfig.color}
            size="small"
            variant="outlined"
            onClick={(e) => openRolePopover(e, row)}
            sx={{ fontSize: 11, height: 20, cursor: "pointer" }}
          />
        );
      },
    },
    {
      id: "actions",
      label: "",
      width: 52,
      align: "center",
      render: (row) => (
        <Tooltip title="რედაქტირება">
          <IconButton
            size="small"
            onClick={() => { setSelectedUserForEdit(row); setEditUserDialogOpen(true); }}
            sx={{ p: 0.5, "& svg": { fontSize: 15 } }}
          >
            <EditIcon />
          </IconButton>
        </Tooltip>
      ),
    },
  ];

  return (
    <ProtectedPage permission="USER_MANAGE">
      <Box sx={{ p: 3 }}>
        <UserCreateDialog
          open={editUserDialogOpen}
          setOpen={setEditUserDialogOpen}
          isMonitoring={isMonitoring}
          agencyDepartments={generalFilterData?.agency_departments}
          editUser={selectedUserForEdit}
          onUserUpdated={() => { fetchUsers(); setSelectedUserForEdit(null); }}
        />

        <div className="roles-page__header">
          <div className="roles-page__title-group">
            <BadgeIcon color="primary" sx={{ fontSize: 28 }} />
            <Typography variant="h5" fontWeight={700}>პოზიციები</Typography>
            {total > 0 && (
              <Chip label={total} size="small" color="primary" variant="outlined" sx={{ fontWeight: 600, height: 22, fontSize: 12 }} />
            )}
          </div>

          <div className="roles-page__filters">
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>ტიპი</InputLabel>
              <Select value={filterUserType} label="ტიპი" onChange={(e: SelectChangeEvent) => setFilterUserType(e.target.value)}>
                <MenuItem value=""><em>ყველა</em></MenuItem>
                <MenuItem value="agency">სააგენტო</MenuItem>
                <MenuItem value="monitoring">მონიტ. კომპანია</MenuItem>
                <MenuItem value="admin">ადმინი</MenuItem>
              </Select>
            </FormControl>
            <TextField
              placeholder="ძებნა..."
              size="small"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" color="action" /></InputAdornment> }}
              sx={{ width: 220 }}
            />
          </div>
        </div>

        <DataTable
          columns={columns}
          rows={users}
          rowKey={(row) => row._id}
          loading={loading}
          emptyText="მომხმარებლები არ მოიძებნა"
        />

        {/* Role switcher popover */}
        <Popover
          open={Boolean(popoverAnchor)}
          anchorEl={popoverAnchor}
          onClose={closeRolePopover}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
          transformOrigin={{ vertical: "top", horizontal: "center" }}
        >
          <Stack direction="row" spacing={1} sx={{ p: 1.5 }}>
            <Chip
              label="წევრი"
              color={popoverUser?.org_role === "member" ? "primary" : "default"}
              variant={popoverUser?.org_role === "member" ? "filled" : "outlined"}
              size="small"
              onClick={() => handleSetRole("member")}
              sx={{ cursor: "pointer", fontWeight: popoverUser?.org_role === "member" ? 700 : 400 }}
            />
            <Chip
              label="ხელმძღვანელი"
              color={popoverUser?.org_role === "head" ? "primary" : "default"}
              variant={popoverUser?.org_role === "head" ? "filled" : "outlined"}
              size="small"
              onClick={() => handleSetRole("head")}
              sx={{ cursor: "pointer", fontWeight: popoverUser?.org_role === "head" ? 700 : 400 }}
            />
          </Stack>
        </Popover>
      </Box>
    </ProtectedPage>
  );
}

export default Roles;
